import { useState } from 'react';
import axios from 'axios';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Pencil, Trash, Image as ImageIcon, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useDataCache } from '@/contexts/DataCache';
import { compressImage } from '@/lib/imageCompress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Categories = () => {
  const { categories, refreshCategories } = useDataCache();
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    parent_id: 'none'
  });

  const fetchCategories = async () => {
    await refreshCategories();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const dataUrl = await compressImage(file);
      setFormData({ ...formData, image_url: dataUrl });
    } catch (err) {
      toast.error(err.message || 'No se pudo procesar la imagen');
    } finally {
      e.target.value = '';
    }
  };

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || '',
        image_url: category.image_url || '',
        parent_id: category.parent_id || 'none'
      });
    } else {
      setEditingCategory(null);
      setFormData({ name: '', description: '', image_url: '', parent_id: 'none' });
    }
    setShowDialog(true);
  };

  const handleDelete = async (categoryId) => {
    if (window.confirm('¿Estás seguro de eliminar esta categoría? Las subcategorías quedarán huérfanas.')) {
      try {
        await axios.delete(`${API}/categories/${categoryId}`, { withCredentials: true });
        toast.success('Categoría eliminada');
        fetchCategories();
      } catch (error) {
        toast.error('Error al eliminar categoría');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        parent_id: formData.parent_id === 'none' ? null : formData.parent_id
      };
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, payload, { withCredentials: true });
        toast.success('Categoría actualizada');
      } else {
        await axios.post(`${API}/categories`, payload, { withCredentials: true });
        toast.success('Categoría creada');
      }
      setShowDialog(false);
      fetchCategories();
    } catch (error) {
      toast.error('Error al guardar categoría');
    }
  };

  const parentCategories = categories.filter((c) => !c.parent_id);
  const getSubcategoriesOf = (parentId) => categories.filter((c) => c.parent_id === parentId);

  // Para evitar elegir como padre la propia categoría o sus descendientes (mantener jerarquía simple)
  const availableParentOptions = categories.filter((c) => {
    if (!editingCategory) return !c.parent_id; // sólo categorías raíz pueden ser padres (1 nivel)
    return c.id !== editingCategory.id && !c.parent_id;
  });

  const renderCategoryCard = (category, isSub = false) => (
    <div
      key={category.id}
      className={`bg-white border border-border rounded-sm overflow-hidden hover:shadow-md transition-shadow ${isSub ? 'border-l-4 border-l-primary/40' : ''}`}
      data-testid={`category-card-${category.id}`}
    >
      <div className="aspect-square bg-slate-100 flex items-center justify-center relative">
        {category.image_url ? (
          <img src={category.image_url} alt={category.name} className="w-full h-full object-cover" />
        ) : (
          <ImageIcon size={48} className="text-slate-400" />
        )}
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-lg mb-1">
          {isSub && <span className="text-xs text-muted-foreground mr-1">↳</span>}
          {category.name}
        </h3>
        {category.description && (
          <p className="text-sm text-muted-foreground mb-3">{category.description}</p>
        )}
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleOpenDialog(category)}
            data-testid={`edit-category-${category.id}`}
            className="flex-1"
          >
            <Pencil size={16} className="mr-2" />
            Editar
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => handleDelete(category.id)}
            data-testid={`delete-category-${category.id}`}
          >
            <Trash size={16} />
          </Button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <p>Cargando...</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Categorías</h1>
            <p className="text-muted-foreground mt-1">Organiza tus productos por categorías y subcategorías</p>
          </div>
          <Button onClick={() => handleOpenDialog()} data-testid="add-category-button">
            <Plus size={20} weight="bold" className="mr-2" />
            Nueva Categoría
          </Button>
        </div>

        <div className="space-y-8">
          {parentCategories.map((parent) => {
            const subs = getSubcategoriesOf(parent.id);
            return (
              <div key={parent.id} className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {renderCategoryCard(parent, false)}
                </div>
                {subs.length > 0 && (
                  <div className="ml-4 pl-4 border-l-2 border-dashed border-primary/30">
                    <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wider">Subcategorías de {parent.name}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {subs.map((sub) => renderCategoryCard(sub, true))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {/* Categorías huérfanas (parent_id apunta a algo eliminado) */}
          {categories.filter((c) => c.parent_id && !categories.some((p) => p.id === c.parent_id)).length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wider">Sin categoría padre</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {categories
                  .filter((c) => c.parent_id && !categories.some((p) => p.id === c.parent_id))
                  .map((c) => renderCategoryCard(c, true))}
              </div>
            </div>
          )}
          {categories.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">No hay categorías registradas</div>
          )}
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="category-form">
            <div>
              <Label>Imagen de Categoría</Label>
              <div className="mt-2 flex items-center gap-4">
                {formData.image_url ? (
                  <div className="relative">
                    <img src={formData.image_url} alt="Preview" className="w-32 h-32 object-cover rounded border" />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image_url: '' })}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="w-32 h-32 bg-slate-100 rounded border flex items-center justify-center">
                    <ImageIcon size={32} className="text-slate-400" />
                  </div>
                )}
                <Input type="file" accept="image/*,.heic,.heif" onChange={handleImageUpload} className="max-w-xs" />
              </div>
            </div>
            <div>
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="category-name-input"
                required
              />
            </div>
            <div>
              <Label>Categoría Padre (Opcional)</Label>
              <Select
                value={formData.parent_id}
                onValueChange={(value) => setFormData({ ...formData, parent_id: value })}
              >
                <SelectTrigger data-testid="category-parent-select">
                  <SelectValue placeholder="Sin padre (categoría raíz)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin padre (categoría raíz)</SelectItem>
                  {availableParentOptions.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Si seleccionas una categoría padre, ésta será una subcategoría.
              </p>
            </div>
            <div>
              <Label htmlFor="description">Descripción</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                data-testid="category-description-input"
              />
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-category-button">
                {editingCategory ? 'Actualizar' : 'Crear'} Categoría
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};
